<script lang="ts" setup>
  import { computed } from 'vue';
  import type { Currency } from '@storefront/resources';
  import { useShared } from '@othercommerce/foundation';

  type OrderSummary = {
    currency: Currency;
    discountGross: number | null;
    itemsGross: number;
    loyaltyValue: number | null;
    shippingOptionService: string | null;
    shippingCost: number;
    totalGross: number;
  }

  interface Props {
    order: OrderSummary | null | undefined;
  }

  const props = defineProps<Props>();
  const shared = useShared();

  const code = computed(() => props.order?.currency.code || shared.value.appCurrency);
  const decimals = computed(() => props.order?.currency.decimals || 2);
  const items = computed(() => props.order?.itemsGross || 0);
  const discount = computed(() => props.order?.discountGross || 0);
  const points = computed(() => props.order?.loyaltyValue || 0);
  const shipping = computed(() => props.order?.shippingCost || 0);
  const total = computed(() => props.order?.totalGross || 0);
</script>
<template>
  <div class="order-cart-summary">
    <dl class="order-cart-summary__wrapper">
      <div class="order-cart-summary__item order-cart-summary__products">
        <dt class="order-cart-summary__item-term">
          {{ $t('SHOP.ORDER.LABEL.ITEMS_TOTAL') }}
        </dt>
        <dd class="order-cart-summary__item-value">
          <SfMoney :value="items" v-bind="{ code, decimals }" />
        </dd>
      </div>
      <div v-if="order?.shippingOptionService" :class="{ 'order-cart-summary__item--discount': shipping <= 0 }" class="order-cart-summary__item delivery">
        <dt class="order-cart-summary__item-term">
          {{ $t('SHOP.ORDER.LABEL.SHIPPING_COST') }}
        </dt>
        <dd class="order-cart-summary__item-value">
          <SfMoney :value="shipping" v-bind="{ code, decimals }" />
        </dd>
      </div>
      <div v-if="discount > 0" class="order-cart-summary__item order-cart-summary__item--discount">
        <dt class="order-cart-summary__item-term">
          {{ $t('SHOP.ORDER.LABEL.DISCOUNT') }}
        </dt>
        <dd class="order-cart-summary__item-value">
          <SfMoney :value="discount * -1" v-bind="{ code, decimals }" />
        </dd>
      </div>
      <div v-if="points > 0" class="order-cart-summary__item order-cart-summary__item--discount">
        <dt class="order-cart-summary__item-term">
          {{ $t('SHOP.ORDER.LABEL.LOYALTY_POINTS') }}
        </dt>
        <dd class="order-cart-summary__item-value">
          <SfMoney :value="points * -1" v-bind="{ code, decimals }" />
        </dd>
      </div>
      <div class="order-cart-summary__item order-cart-summary__final">
        <dt class="order-cart-summary__item-term">
          {{ $t('SHOP.ORDER.LABEL.ORDER_TOTAL') }}
          <span class="order-cart-summary__final-vat">{{ $t('SHOP.ORDER.LABEL.INCL_VAT') }}</span>
        </dt>
        <dd class="order-cart-summary__item-value order-cart-summary__item-value--summary">
          <SfMoney :value="total" v-bind="{ code, decimals }" />
        </dd>
      </div>
    </dl>
    <slot />
  </div>
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-order-cart-summary-border-color: var(--secondary-border);
    --sf-order-cart-summary-vat-color: var(--secondary-color);
    --sf-order-cart-summary-discount-color: var(--sf-status-color-success);
  }

  .order-cart-summary {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__wrapper {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      margin-bottom: 0.5rem;
      font-size: 1rem;
    }

    &__item {
      display: flex;
      justify-content: space-between;
      line-height: 1.25rem;
    }

    &__final {
      padding: 1rem 0;
      border-top: 1px solid var(--sf-order-cart-summary-border-color);
      font-weight: var(--font-weight-bold);
      font-size: 1.25rem;
      line-height: 1;
    }

    &__final-vat {
      display: block;
      color: var(--sf-order-cart-summary-vat-color);
      font-size: 1rem;
      font-weight: var(--font-weight-regular);
      margin-top: 2px;
    }

    &__item-value {
      font-weight: var(--font-weight-semi);

      &--summary {
        font-weight: var(--font-weight-bold);
      }
    }

    &__item--discount {
      color: var(--sf-order-cart-summary-discount-color);
    }
  }

  @include media.query(lg) {

    .order-cart-summary {
      &__wrapper {
        margin-bottom: 0;
        gap: 0.375rem;
      }

      &__item {
        font-size: 0.875rem;
        line-height: 1rem;
      }

      &__final {
        padding-top: 0.875rem;
        padding-bottom: 1rem;
        font-size: 1rem;
      }
    }
  }
</style>
